import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import axios from 'axios';
import { fetchMasterData, shouldRefreshMasterData, resetState as resetMasterData } from './masterDataSlice';
import { API_BASE_URL } from '../../utils/apiConfig';

const postLogin = (loginID, password, role) =>
  axios.post(`${API_BASE_URL}/auth/login`, {
    loginID,
    password,
    ...(role ? { role } : {})
  });

const isLegacyRoleRequired = (error) =>
  error.response?.status === 400 &&
  error.response?.data?.errors?.some((e) => e.path === 'role');

// Login user
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ loginID, password }, { dispatch, rejectWithValue }) => {
    try {
      let response;
      try {
        response = await postLogin(loginID, password);
      } catch (error) {
        if (!isLegacyRoleRequired(error)) {
          throw error;
        }
        // Older deployed API still requires role — try both without asking the user
        let lastError = error;
        for (const role of ['staff', 'admin']) {
          try {
            response = await postLogin(loginID, password, role);
            lastError = null;
            break;
          } catch (attemptError) {
            lastError = attemptError;
            if (attemptError.response?.status !== 401) {
              throw attemptError;
            }
          }
        }
        if (!response) {
          throw lastError;
        }
      }

      const { token, user } = response.data;
      // Store token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Fetch master data immediately after successful login
      await dispatch(fetchMasterData()).unwrap();
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 401 && error.response?.data?.message === 'Invalid credentials') {
        return rejectWithValue('Wrong credentials');
      }
      const validationMsg = error.response?.data?.errors?.[0]?.msg;
      return rejectWithValue(
        error.response?.data?.message || validationMsg || 'Login failed. Please try again.'
      );
    }
  }
);

// Verify token
export const verifyToken = createAsyncThunk(
  'auth/verify',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await axios.post(
        `${API_BASE_URL}/auth/verify`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update localStorage with fresh user data
      localStorage.setItem('user', JSON.stringify(response.data.user));

      const { masterData } = getState();
      if (shouldRefreshMasterData(masterData)) {
        await dispatch(fetchMasterData()).unwrap();
      }

      return response.data;
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return rejectWithValue('Session expired. Please login again.');
    }
  }
);

// Initialize app (for page refresh scenarios)
export const initializeApp = createAsyncThunk(
  'auth/initialize',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');

      // If no token, return null (user needs to login)
      if (!token) {
        return null;
      }

      // Always verify token on app load to get fresh user data (including permissions)
      // so that permission changes (e.g. position updates) take effect after refresh
      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/verify`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const authData = {
          success: true,
          user: response.data.user,
          token: token
        };

        localStorage.setItem('user', JSON.stringify(response.data.user));

        // Refresh master data, but never let a master-data failure/cancellation
        // drop an otherwise-valid session (unwrap() rejects on the thunk's
        // in-flight condition, which must not be treated as an auth failure).
        const { masterData } = getState();
        if (shouldRefreshMasterData(masterData)) {
          try {
            await dispatch(fetchMasterData()).unwrap();
          } catch (masterDataError) {
            console.warn('Master data refresh failed during init:', masterDataError?.message);
          }
        }

        return authData;
      } catch (verifyError) {
        // Only end the session when the server explicitly rejects the token.
        const status = verifyError.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          return rejectWithValue('Session expired. Please login again.');
        }

        // Transient failure (network/5xx/CORS): keep the existing session using
        // cached user data instead of logging the user out.
        console.warn('Token verification could not complete; keeping cached session:', verifyError?.message);
        let cachedUser = null;
        try {
          cachedUser = JSON.parse(localStorage.getItem('user') || 'null');
        } catch {
          cachedUser = null;
        }
        return { success: true, user: cachedUser, token };
      }
    } catch (error) {
      console.error('Initialize app error:', error);
      return rejectWithValue('Session expired. Please login again.');
    }
  }
);

const initialState = {
  user: (() => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const parsedUser = JSON.parse(user);
        // Ensure user object has staff_id field (for backward compatibility)
        if (typeof parsedUser.staff_id === 'undefined') {
          parsedUser.staff_id = null;
        }
        return parsedUser;
      }
      return null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      localStorage.removeItem('user');
      return null;
    }
  })(),
  token: localStorage.getItem('token'),
  // Block protected routes until token verification completes on refresh
  isLoading: !!localStorage.getItem('token'),
  isAuthenticated: (() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return !!(token && user);
  })(),
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state, action) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      
      // Clear master data and dashboard when logging out
      // Note: We'll dispatch resetMasterData in the component that calls logout
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Reconcile persisted auth with the real token after redux-persist rehydration.
      // Persisted state can leave isAuthenticated=true (and isLoading=false) while the
      // localStorage token is missing/stale. Without this, protected pages render and
      // fire authenticated requests before initializeApp verifies the token — causing a
      // spurious "No token, authorization denied" logout on reopen.
      .addCase(REHYDRATE, (state, action) => {
        const rehydrated = action.payload?.auth || {};
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');

        if (!token || !user) {
          // No valid session on disk — clear any stale persisted auth.
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
          state.isLoading = false;
          state.error = null;
          return;
        }

        // A token exists: keep the session but block protected routes until
        // AppInitializer's token verification completes.
        state.user = rehydrated.user || state.user;
        state.token = token;
        state.isAuthenticated = true;
        state.isLoading = true;
        state.error = null;
      })
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
      })
      // Verify token cases
      .addCase(verifyToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
      })
      .addCase(verifyToken.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      // Initialize app cases
      .addCase(initializeApp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeApp.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
        } else {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
        }
      })
      .addCase(initializeApp.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer; 